import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AlertTriangle, ShieldCheck, AlertCircle, CheckCircle2, RefreshCw, Users, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function normalizeName(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().replace(/\s+/g, ' ');
}

function tokenSet(s) {
  return new Set(normalizeName(s).split(' ').filter(t => t.length > 1));
}

function nameOverlap(a, b) {
  const ta = tokenSet(a), tb = tokenSet(b);
  if (!ta.size || !tb.size) return { overlap: 0, total: 0 };
  let overlap = 0;
  ta.forEach(t => { if (tb.has(t)) overlap++; });
  return { overlap, total: Math.min(ta.size, tb.size) };
}

export function computeVerificationDiscrepancies(verification, user, lang = 'en') {
  const tt = (en, es) => (lang === 'es' ? es : en);
  const ai = verification?.ai_result || {};
  const role = verification?.role;
  const issues = [];

  if (ai.face_match === 'no_match') {
    issues.push({
      type: 'face_no_match',
      severity: 'high',
      label: tt('Selfie ≠ Cédula photo', 'Selfie ≠ foto Cédula'),
      detail: tt('AI flagged the selfie as a different person from the Cédula.',
                 'La IA marcó la selfie como persona diferente a la Cédula.'),
    });
  }

  if (user?.name && ai.cedula_name) {
    const { overlap, total } = nameOverlap(user.name, ai.cedula_name);
    if (overlap === 0) {
      issues.push({
        type: 'name_mismatch',
        severity: 'high',
        label: tt('Registered name does not match Cédula', 'El nombre registrado no coincide con la Cédula'),
        detail: `"${user.name}" ≠ "${ai.cedula_name}"`,
        actionable: 'sync_name',
        cedula_name: ai.cedula_name,
      });
    } else if (overlap < total) {
      issues.push({
        type: 'name_partial',
        severity: 'low',
        label: tt('Partial name match with Cédula', 'Coincidencia parcial con Cédula'),
        detail: `"${user.name}" vs "${ai.cedula_name}"`,
        actionable: 'sync_name',
        cedula_name: ai.cedula_name,
      });
    }
  }

  if (ai.names_consistent === false && Array.isArray(ai.name_matches)) {
    const mismatches = ai.name_matches.filter(nm => nm && nm.matches_cedula === false);
    if (mismatches.length) {
      issues.push({
        type: 'cross_doc_name',
        severity: 'high',
        label: tt(`${mismatches.length} document(s) name mismatch`, `${mismatches.length} documento(s) con nombre distinto`),
        detail: mismatches.map(m => `${m.document}: "${m.name_found}"`).join(' · '),
      });
    }
  }

  if (ai.confidence === 'low') {
    issues.push({
      type: 'low_confidence',
      severity: 'low',
      label: tt('AI confidence: low', 'Confianza IA: baja'),
      detail: ai.notes || '',
    });
  }

  const vdata = user?.verification_data;
  if (vdata && vdata._ai_extracted_at) {
    const required = role === 'agent'
      ? ['civ_membership', 'rif', 'business_registration', 'cedula']
      : role === 'lawyer'
        ? ['colegiado_number', 'bar_association', 'cedula', 'university_degree']
        : [];
    const missing = required.filter(f => !(vdata[f] && String(vdata[f]).trim()));
    if (missing.length) {
      issues.push({
        type: 'missing_fields',
        severity: 'low',
        label: tt(`${missing.length} field(s) not auto-extracted`, `${missing.length} campo(s) no extraídos`),
        detail: missing.join(', '),
      });
    }
  }

  // Duplicate Cédula already on file under another user
  const dups = Array.isArray(verification?.cedula_duplicates) ? verification.cedula_duplicates : [];
  if (dups.length) {
    issues.push({
      type: 'duplicate_cedula',
      severity: 'high',
      label: tt(
        `Cédula already on file (${dups.length} other ${dups.length === 1 ? 'user' : 'users'})`,
        `Cédula ya registrada (${dups.length} otro${dups.length === 1 ? '' : 's'})`,
      ),
      detail: dups.map(d => `${d.name || d.email} (${d.role}) — ${d.cedula}`).join(' · '),
      duplicates: dups,
    });
  }

  return issues;
}

export default function VerificationDiscrepancyBadge({ verification, user, onResolved }) {
  const { token } = useAuth();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const issues = computeVerificationDiscrepancies(verification, user, language);

  if (!verification?.ai_result || !Object.keys(verification.ai_result).length) return null;

  const highCount = issues.filter(i => i.severity === 'high').length;
  const lowCount = issues.filter(i => i.severity === 'low').length;
  const total = issues.length;

  if (total === 0) {
    return (
      <Badge variant="outline" className="gap-1 text-[10px] border-green-200 bg-green-50 text-green-700" data-testid="vdiscrepancy-clean">
        <ShieldCheck className="w-3 h-3" />
        {tt('No discrepancies', 'Sin discrepancias')}
      </Badge>
    );
  }

  const isHigh = highCount > 0;

  const onSyncName = async (cedula_name) => {
    setSyncing(true);
    try {
      await axios.post(`${API}/verification/admin/${verification.user_id}/sync-name`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(tt(`Name updated to "${cedula_name}"`, `Nombre actualizado a "${cedula_name}"`));
      setOpen(false);
      if (onResolved) onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || tt('Sync failed', 'Error al sincronizar'));
    } finally { setSyncing(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
          className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border transition-colors cursor-pointer
            ${isHigh ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100' : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'}`}
          data-testid="vdiscrepancy-badge">
          {isHigh ? <AlertTriangle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
          <span>{total} {tt('discrepancy', total === 1 ? 'discrepancia' : 'discrepancias')}</span>
          {highCount > 0 && (
            <span className="ml-0.5 bg-red-600 text-white rounded-full px-1 text-[9px]" data-testid="vdiscrepancy-high-count">{highCount}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0" data-testid="vdiscrepancy-popover">
        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-white">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${isHigh ? 'text-red-600' : 'text-amber-600'}`} />
            <h4 className="font-heading font-semibold text-slate-900 text-sm">{tt('Discrepancies detected', 'Discrepancias detectadas')}</h4>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            {highCount > 0 && <span className="text-red-600 font-semibold">{highCount} {tt('critical', 'críticas')}</span>}
            {highCount > 0 && lowCount > 0 && <span> · </span>}
            {lowCount > 0 && <span className="text-amber-600 font-semibold">{lowCount} {tt('minor', 'menores')}</span>}
          </p>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {issues.map((iss, idx) => (
            <div key={idx} className="px-4 py-2.5 hover:bg-slate-50" data-testid={`vdiscrepancy-issue-${idx}`}>
              <div className="flex items-start gap-2">
                {iss.severity === 'high'
                  ? <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                  : <AlertCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-medium ${iss.severity === 'high' ? 'text-red-700' : 'text-amber-700'}`}>{iss.label}</p>
                  {iss.detail && <p className="text-[11px] text-slate-500 mt-0.5 break-words">{iss.detail}</p>}

                  {iss.actionable === 'sync_name' && iss.cedula_name && (
                    <Button size="sm" variant="outline" disabled={syncing}
                      onClick={() => onSyncName(iss.cedula_name)}
                      className="mt-2 h-7 text-[10px] gap-1 border-[#D4AF37] text-[#0B192C] hover:bg-[#D4AF37]/10"
                      data-testid={`vdiscrepancy-sync-${idx}`}>
                      {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      {tt('Sync name to Cédula', 'Sincronizar nombre con Cédula')}
                    </Button>
                  )}

                  {iss.type === 'duplicate_cedula' && Array.isArray(iss.duplicates) && iss.duplicates.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {iss.duplicates.map(d => (
                        <div key={d.user_id} className="flex items-center gap-1.5 text-[11px] bg-red-50 border border-red-100 rounded-md px-2 py-1">
                          <Users className="w-3 h-3 text-red-500 flex-shrink-0" />
                          <span className="font-medium text-red-700 truncate">{d.name || d.email}</span>
                          <span className="text-red-400">·</span>
                          <span className="text-red-500 capitalize">{d.role}</span>
                          <span className="text-red-400">·</span>
                          <span className="text-red-500 font-mono">{d.cedula}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          {tt('Review documents and AI result above before approving.', 'Revisa los documentos y el resultado IA antes de aprobar.')}
        </div>
      </PopoverContent>
    </Popover>
  );
}
